/**
 * Util function to fire the `credit_card_browse_page_click` CRM event
 * for the CC Browse Abandon campaign (Q4 2020)
 * JIRA: https://nerdwallet.atlassian.net/browse/SHOP-13880
 *
 * `credit_card_browse_page_click` is fired to indicate that a logged-in
 * user mClicked on one of the pages included in CC_BROWSE_PAGE_PATHS
 */

import _ from 'lodash';
import appConfig from '@nerdwallet/app-config';
import { getCookies } from '@nerdwallet/nw-api-sdk/cookies';
import { getLogger } from '@nerdwallet/logging';
import { httpClient } from './nw-api-sdk';

const logger = getLogger('credit_card_browse_page_click');

/**
 * Paths of pages that are in scope for the CC Browse Abandon Campaign
 */
const CC_BROWSE_PAGE_PATHS = {
  // CC compare page
  COMPARE_PAGE: '/compare/credit-cards',
  // subset of CC roundup pages
  ROUNDUP_REWARDS: '/best/credit-cards/rewards',
  ROUNDUP_GAS: '/best/credit-cards/gas',
  ROUNDUP_CASH_BACK: '/best/credit-cards/cash-back',
  ROUNDUP_HOTEL: '/best/credit-cards/hotel',
  ROUNDUP_AIRLINE: '/best/credit-cards/airline',
  ROUNDUP_TRAVEL: '/best/credit-cards/travel',
  ROUNDUP_BONUS_OFFERS: '/best/credit-cards/bonus-offers',
  ROUNDUP_LOW_INTEREST: '/best/credit-cards/low-interest',
  ROUNDUP_SECURED: '/best/credit-cards/secured',
  ROUNDUP_BALANCE_TRANSFER: '/best/credit-cards/balance-transfer',
  ROUNDUP_BAD_CREDIT: '/best/credit-cards/bad-credit',
  ROUNDUP_SMALL_BUSINESS: '/best/credit-cards/small-business',
  ROUNDUP_COLLEGE_STUDENT: '/best/credit-cards/college-student',
  // CC review pages (targets all URLs of the format `/reviews/credit-cards/<slug>`)
  REVIEWS: '/reviews/credit-cards/',
};

const CC_BROWSE_CATEGORIES = {
  REWARDS: 'REWARDS',
  TRAVEL: 'TRAVEL',
  GENERAL: 'GENERAL',
  LOW_INTEREST: 'LOW_INTEREST',
  BALANCE_TRANSFER: 'BALANCE_TRANSFER',
  SECURED: 'SECURED',
  BUSINESS: 'BUSINESS',
  STUDENT: 'STUDENT',
  REVIEWS: 'REVIEWS',
};

/**
 * Given a `ccBrowsePageEnum`, return its category.
 * If the given ccBrowsePageEnum path doesn't map to a category, return 'NOT_DEFINED'
 *
 * This is a hardcoded mapping determined by CRM here:
 * https://docs.google.com/spreadsheets/d/1gNa33_hzRN2kVN1L2h6SxIRlBoHyrVXz66l77F_Wn3k/edit#gid=0
 *
 * @param {String} ccBrowsePageEnum - one of the keys in `CC_BROWSE_PAGE_PATHS`
 * @returns {String} an enum in `CC_BROWSE_CATEGORIES`
 */
const getCreditCardBrowseCategory = (ccBrowsePageEnum) => {
  switch (ccBrowsePageEnum) {
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_REWARDS:
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_GAS:
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_CASH_BACK:
      return CC_BROWSE_CATEGORIES.REWARDS;
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_HOTEL:
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_AIRLINE:
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_TRAVEL:
      return CC_BROWSE_CATEGORIES.TRAVEL;
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_BONUS_OFFERS:
    case CC_BROWSE_PAGE_PATHS.COMPARE_PAGE:
      return CC_BROWSE_CATEGORIES.GENERAL;
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_LOW_INTEREST:
      return CC_BROWSE_CATEGORIES.LOW_INTEREST;
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_BALANCE_TRANSFER:
      return CC_BROWSE_CATEGORIES.BALANCE_TRANSFER;
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_SECURED:
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_BAD_CREDIT:
      return CC_BROWSE_CATEGORIES.SECURED;
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_SMALL_BUSINESS:
      return CC_BROWSE_CATEGORIES.BUSINESS;
    case CC_BROWSE_PAGE_PATHS.ROUNDUP_COLLEGE_STUDENT:
      return CC_BROWSE_CATEGORIES.STUDENT;
    case CC_BROWSE_PAGE_PATHS.REVIEWS:
      return CC_BROWSE_CATEGORIES.REVIEWS;
    default:
      return 'NOT_DEFINED';
  }
};

/**
 * The main function for firing the credit_card_browse_page_click event
 * Schema definition: https://github.com/NerdWallet/app-schemata/blob/aab041398eeabb529102b614862a74acaf40bb9e/yak_message_payloads/credit_cards/credit_card_browse_page_click.yaml
 */
const publishCreditCardBrowsePageClick = async () => {
  // Ensure the `window` object exists because this call occurs client-side
  if (!window?.location?.search) {
    return;
  }

  // Get the pageURL from the `source_url` query params
  // Return if `source_url` is missing, or parsing `source_url` throws an error
  let pageURL;
  try {
    const params = new URLSearchParams(window.location.search);
    const sourceURL = params.get('source_url');
    if (!sourceURL) {
      // `source_url` is missing
      return;
    }
    const url = new URL(sourceURL);
    pageURL = url.origin + url.pathname;
  } catch (e) {
    logger.error('error parsing source_url', e);
    return;
  }

  // Get the appropriate category for this URL (e.g. 'REVIEWS', 'TRAVEL')
  const ccBrowsePageEnum = _.find(CC_BROWSE_PAGE_PATHS, (path) =>
    _.includes(pageURL, path)
  );
  const creditCardBrowseCategory =
    getCreditCardBrowseCategory(ccBrowsePageEnum);

  /**
   * If a user has `member_identity_assertion_token` set in their cookies, they _may_ be logged in.
   * This cookie has the same lifecycle as `access_token`.
   * `member_identity_assertion_token` is used because `access_token` is an HttpOnly cookie (not available client-side)
   *
   * Reference to `member_identity_assertion_token` documentation: https://nerdwallet.atlassian.net/wiki/spaces/ENG/pages/570753072/2019-06+Identity+Assertion+Token+Project+Spec
   *
   * This is an alternative lighter solution to calling identity to check that a user is logged in.
   * This will lead to some false positives (we think a user is logged in when they are not), but in this case
   * the `publishCRMEvent` mutation will return a 401, since it performs identity lookup
   * code ref: https://github.com/NerdWallet/query0/blob/master/src/schema/crm/schema.js#L75-L76
   */
  let cookies;
  try {
    cookies = await getCookies();
  } catch (e) {
    logger.error('error getting cookies');
  }
  const hasMemberIdentityAssertionToken = _.get(
    cookies,
    'member_identity_assertion_token'
  );

  if (
    !hasMemberIdentityAssertionToken ||
    creditCardBrowseCategory === 'NOT_DEFINED'
  ) {
    return;
  }
  // create the custom payload for the CRM event
  const customPayload = {
    category: creditCardBrowseCategory,
    page_url: pageURL,
  };

  // call the query0 mutation:
  // https://github.com/NerdWallet/query0/blob/master/src/schema/crm/schema.js#L22-L29
  const operationName = 'PublishCRMEvent';
  try {
    const { body } = await httpClient.fetch({
      url: appConfig.API.BASE_URLS.QUERY0,
      method: 'POST',
      headers: {
        'X-Client-Platform': 'web',
      },
      json: {
        operationName,
        query: `mutation ${operationName}($eventName: String!, $customPayload: JSON) {
            publishCRMEvent(eventName: $eventName, customPayload: $customPayload) {
              success
            }
          }
        `,
        variables: {
          eventName: 'credit_card_browse_page_click',
          customPayload,
        },
      },
    });
    const { errors } = body;
    if (errors) {
      // this is for errors specific to the `publishCRMEvent` (located at body.errors)
      // this will happen if there is an error in the downstream service that query0 calls
      logger.error('publishCRMEvent error', {
        customPayload,
        query0ErrorMessage: _.get(errors, '0.message'),
      });
    } else {
      logger.info('success', {
        customPayload,
      });
    }
  } catch (e) {
    // This wraps the entire httpClient call.
    // This will catch errors that occur if there is an issue reaching query0 (errors that aren't specific to publishCRMEvent)
    // httpClient has a default timeout of 5000ms (https://github.com/NerdWallet/js-nw-api-sdk/blob/7b9024e443b327e8b70815d4e343454572664e01/src/http-client.js#L38)
    logger.error('query0 error', e);
  }
};

export default publishCreditCardBrowsePageClick;
